import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  CheckCircle, Clock, RotateCcw, Calendar, 
  FileCheck, ChevronDown, ChevronUp, MessageSquare, User
} from 'lucide-react';

/**
 * Composant de consultation EPMSP (lecture seule)
 * Affiche les résultats des épreuves EPMSP pour un stagiaire
 * Utilisé par : Tuteur, Formateur, Entreprise, Stagiaire
 * 
 * Props:
 * - stagiaireId: ID du stagiaire (mode direct)
 * - stagiaireNom: Nom du stagiaire (optionnel)
 * - stagiaires: Liste des stagiaires pour le mode sélection (utilisé par le formateur)
 */
const EPMSPConsultation = ({ stagiaireId: propStagiaireId, stagiaireNom, stagiaires = [] }) => {
  const [selectedStagiaireId, setSelectedStagiaireId] = useState(propStagiaireId || '');
  const [epmspData, setEpmspData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Mode sélecteur si une liste de stagiaires est fournie et pas de stagiaireId direct
  const isSelectionMode = stagiaires.length > 0 && !propStagiaireId;

  // Les 4 épreuves EPMSP
  const EPREUVES = [
    "Séances de découverte VNM à selle",
    "Traction d'un engin tracté",
    "Situation de secours",
    "Séance d'initiation sur support au choix (ESH/PNM/Jet à Bras)"
  ];

  // Utiliser le stagiaireId de prop ou celui sélectionné
  const currentStagiaireId = propStagiaireId || selectedStagiaireId;

  useEffect(() => {
    if (currentStagiaireId) {
      loadEPMSPData();
    } else {
      setEpmspData(null);
      setLoading(false);
    }
  }, [currentStagiaireId]);

  const loadEPMSPData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/epmsp/stagiaire/${currentStagiaireId}`);
      setEpmspData(response.data);
    } catch (error) {
      setEpmspData(null);
    } finally {
      setLoading(false);
    }
  };

  // Trouver le nom du stagiaire sélectionné
  const getSelectedStagiaireName = () => {
    if (stagiaireNom) return stagiaireNom;
    const found = stagiaires.find(s => s.user_id === selectedStagiaireId);
    return found ? `${found.prenom} ${found.nom}` : '';
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'valide':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <CheckCircle className="h-3 w-3" />
            Validé
          </span>
        );
      case 'rattrapage':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
            <RotateCcw className="h-3 w-3" />
            Rattrapage requis
          </span>
        );
      case 'rattrapage_valide':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <CheckCircle className="h-3 w-3" />
            Validé (2ème passage)
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
            <Clock className="h-3 w-3" />
            En attente
          </span>
        );
    }
  };

  const validatedCount = epmspData?.validated_count || 0;
  const totalEpreuves = 4;
  const isComplete = validatedCount === totalEpreuves;
  const displayName = getSelectedStagiaireName();

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="epmsp-consultation">
      {/* Titre et sélecteur de stagiaire si mode sélection */}
      {isSelectionMode && (
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-slate-100">
              <FileCheck className="h-5 w-5 text-slate-500" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">Épreuves EPMSP</h3>
              <p className="text-xs text-slate-500">Validation obligatoire avant le face à face pédagogique</p>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
              <User className="h-4 w-4" /> Stagiaire
            </label>
            <select
              value={selectedStagiaireId}
              onChange={(e) => setSelectedStagiaireId(e.target.value)}
              className="w-full md:w-1/2 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-stagiaire-epmsp"
            >
              <option value="">Sélectionner un stagiaire</option>
              {stagiaires.map(s => (
                <option key={s.user_id} value={s.user_id}>
                  {s.prenom} {s.nom}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Message si aucun stagiaire sélectionné en mode sélection */}
      {isSelectionMode && !selectedStagiaireId && (
        <div className="p-6 flex items-center justify-center text-slate-400">
          <p>Sélectionnez un stagiaire pour voir ses épreuves EPMSP</p>
        </div>
      )}

      {/* Afficher le contenu seulement si un stagiaire est sélectionné */}
      {currentStagiaireId && (
        <>
          {/* Header cliquable */}
          <div 
            className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${isComplete ? 'bg-green-50' : ''}`}
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <FileCheck className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">Épreuves EPMSP</h3>
                  <p className="text-xs text-slate-500">
                    {displayName ? `${displayName} - ` : ''}
                    Validation obligatoire avant le face à face pédagogique
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Chargement */}
                {loading ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#8DC63F]"></div>
                    <span className="text-sm">Chargement...</span>
                  </div>
                ) : (
                  <>
                    {/* Progression */}
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${isComplete ? 'bg-green-500' : 'bg-orange-400'}`}
                          style={{ width: `${(validatedCount / totalEpreuves) * 100}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${isComplete ? 'text-green-600' : 'text-slate-600'}`}>
                        {validatedCount}/{totalEpreuves}
                      </span>
                    </div>
                    {isComplete && (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                        Prêt pour le stage
                      </span>
                    )}
                  </>
                )}
                {expanded ? (
                  <ChevronUp className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </div>
          </div>

      {/* Détail des épreuves */}
      {expanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50">
          <div className="space-y-3">
            {EPREUVES.map((epreuve, index) => {
              const validation = epmspData?.epreuves?.[epreuve] || { statut: 'en_attente' };
              return (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    validation.statut === 'valide' || validation.statut === 'rattrapage_valide'
                      ? 'bg-green-50 border-green-200'
                      : validation.statut === 'rattrapage'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-[#0F172A]">
                        {index + 1}. {epreuve}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {getStatusBadge(validation.statut)}
                        
                        {validation.date_epreuve && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <Calendar className="h-3 w-3" />
                            {new Date(validation.date_epreuve).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        
                        {validation.support_choisi && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                            {validation.support_choisi}
                          </span>
                        )}
                      </div>
                      
                      {/* Commentaire si présent */}
                      {validation.commentaire && (
                        <div className="mt-2 flex items-start gap-2 text-xs text-slate-600 bg-slate-100 p-2 rounded">
                          <MessageSquare className="h-3 w-3 mt-0.5 text-slate-400 flex-shrink-0" />
                          <span>{validation.commentaire}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {!epmspData && (
            <p className="text-sm text-slate-500 text-center py-4">
              Aucune épreuve EPMSP enregistrée pour ce stagiaire.
            </p>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default EPMSPConsultation;
